import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe, Flame } from "lucide-react";
import {
  DIGITAL_ASSET_OPTIONS,
  FUNERAL_OPTIONS,
} from "@/components/wizard/validation";

export default function InstructionsStep({
  specialInstructions,
  setSpecialInstructions,
  digitalAssets,
  setDigitalAssets,
  funeralWishes,
  setFuneralWishes,
}) {
  const toggleDigital = (label) => {
    setDigitalAssets((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  return (
    <div data-testid="step-instructions">
      <h2 className="font-serif text-2xl text-navy">Final Wishes</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        Optional. Anything you add here makes your Will more complete and personally meaningful.
      </p>

      {/* DIGITAL ASSETS */}
      <Card className="mt-6 border-[#0A1128]/10" data-testid="digital-assets-card">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
              <Globe size={18} />
            </div>
            <div>
              <div className="font-serif text-lg text-navy">Digital Assets</div>
              <div className="text-xs text-[#4A5568]">
                Authorise your Executor to access your online accounts.
              </div>
            </div>
          </div>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {DIGITAL_ASSET_OPTIONS.map((label) => {
              const checked = digitalAssets.includes(label);
              return (
                <label
                  key={label}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors ${
                    checked
                      ? "border-navy bg-navy/[0.04]"
                      : "border-[#0A1128]/15 hover:border-gold"
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleDigital(label)}
                    className="border-navy data-[state=checked]:bg-navy data-[state=checked]:text-gold"
                    data-testid={`digital-asset-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  />
                  <span className="text-navy">{label}</span>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* FUNERAL WISHES */}
      <Card className="mt-4 border-[#0A1128]/10" data-testid="funeral-card">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
              <Flame size={18} />
            </div>
            <div>
              <div className="font-serif text-lg text-navy">Funeral &amp; Last Rites</div>
              <div className="text-xs text-[#4A5568]">
                Structured preferences for your final ceremonies.
              </div>
            </div>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-[240px_1fr]">
            <div>
              <Label htmlFor="funeral-kind">Preference</Label>
              <Select
                value={funeralWishes.kind || ""}
                onValueChange={(v) => setFuneralWishes({ ...funeralWishes, kind: v })}
              >
                <SelectTrigger className="mt-2" data-testid="funeral-kind">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {FUNERAL_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="funeral-custom">Custom instructions (optional)</Label>
              <Textarea
                id="funeral-custom"
                rows={3}
                className="mt-2"
                value={funeralWishes.custom || ""}
                onChange={(e) =>
                  setFuneralWishes({ ...funeralWishes, custom: e.target.value })
                }
                placeholder="e.g. Ashes to be immersed in the Ganga at Varanasi"
                data-testid="funeral-custom"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SPECIAL INSTRUCTIONS */}
      <Card className="mt-4 border-[#0A1128]/10" data-testid="special-instructions-card">
        <CardContent className="p-6 sm:p-8">
          <div className="font-serif text-lg text-navy">Special Instructions</div>
          <p className="mt-1 text-xs text-[#4A5568]">
            Anything else? Charitable donations, heirloom messages, pet care, family notes.
          </p>
          <Textarea
            className="mt-4"
            rows={6}
            value={specialInstructions || ""}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="e.g. Donate Rs.50,000 to Akshaya Patra. My grandmother's gold bangles to my daughter Riya."
            data-testid="instructions-textarea"
          />
        </CardContent>
      </Card>
    </div>
  );
}
